/**
 * Team Page - High-performance team management
 * 
 * Features:
 * - Team member grid with role-based badges
 * - Capacity & role distribution metrics
 * - Role editing & deactivation
 * - Quick-add member modal (mock)
 * - Linear-style list & grid toggles
 */

import { useState } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { 
  getTeamMembersByWorkspace, 
  getTeamStats,
  updateTeamMemberRole,
  deactivateTeamMember
} from '../lib/services/team.service';
import { 
  UsersIcon, 
  UserPlusIcon, 
  EllipsisHorizontalIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ShieldCheckIcon,
  CommandLineIcon,
  LightBulbIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import type { TeamMember, TeamRole } from '../lib/types';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const ROLE_CONFIG: Record<TeamRole, { label: string; color: string; icon: any }> = {
  team_lead: { label: 'Team Lead', color: 'purple', icon: ShieldCheckIcon },
  developer: { label: 'Developer', color: 'blue', icon: CommandLineIcon },
  planner: { label: 'Planner', color: 'amber', icon: LightBulbIcon },
  qa: { label: 'QA Engineer', color: 'emerald', icon: CheckBadgeIcon },
  member: { label: 'Member', color: 'gray', icon: UsersIcon },
};

export function TeamPage() {
  const { currentWorkspace } = useWorkspace();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<TeamRole | 'all'>('all');

  if (!currentWorkspace) return null;

  const members = getTeamMembersByWorkspace(currentWorkspace.id);
  const stats = getTeamStats(currentWorkspace.id);

  const filteredMembers = members.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         m.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || m.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your team members, roles, and workspace access.</p>
        </div>
        <button className="btn-primary">
          <UserPlusIcon className="h-4 w-4" />
          Invite Member
        </button>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-compact bg-white">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Members</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            <p className="text-xs text-gray-500">/ {stats.total} total</p>
          </div>
        </div>
        {Object.entries(stats.byRole).filter(([_, count]) => count > 0).slice(0, 3).map(([role, count]) => {
          const config = ROLE_CONFIG[role as TeamRole];
          return (
            <div key={role} className="card-compact bg-white">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{config.label}</p>
              <div className="flex items-baseline gap-2 mt-1">
                <p className="text-2xl font-bold text-gray-900">{count}</p>
                <div className={clsx('h-1.5 w-1.5 rounded-full', `bg-${config.color}-500`)} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="relative flex-1 w-full">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search by name or email..." 
            className="input pl-10 w-full"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700">
            <FunnelIcon className="h-4 w-4 text-gray-400" />
            <select 
              className="bg-transparent border-none focus:ring-0 p-0 text-sm"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
            >
              <option value="all">All Roles</option>
              {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                <option key={role} value={role}>{config.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredMembers.length === 0 ? (
          <div className="col-span-full py-12 text-center card bg-white">
            <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-900 font-semibold">No members found</p>
            <p className="text-sm text-gray-500">Try adjusting your search or filters.</p>
          </div>
        ) : (
          filteredMembers.map((member) => (
            <MemberCard key={member.id} member={member} />
          ))
        )}
      </div>
    </div>
  );
}

function MemberCard({ member }: { member: TeamMember }) {
  const config = ROLE_CONFIG[member.role];
  const Icon = config.icon;

  return (
    <div className="card bg-white hover:border-primary-300 transition-all group">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="avatar avatar-lg bg-gray-100 text-gray-700 text-lg">
              {member.avatar ? <img src={member.avatar} alt={member.name} /> : member.name.charAt(0)}
            </div>
            {member.isActive && (
              <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 truncate">{member.name}</h3>
            <p className="text-xs text-gray-500 truncate">{member.email}</p>
          </div>
        </div>
        <MemberActions member={member} />
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className={clsx('badge inline-flex items-center gap-1.5', `badge-${config.color === 'gray' ? 'gray' : config.color === 'emerald' ? 'success' : config.color === 'amber' ? 'warning' : config.color === 'blue' ? 'info' : 'purple'}`)}>
            <Icon className="h-3 w-3" />
            {config.label}
          </span>
          {member.department && (
            <span className="text-xs text-gray-400 px-2 py-0.5 rounded-full bg-gray-50">
              {member.department}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-600">
          <p className="font-medium text-gray-900">{member.title || 'Team Member'}</p>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-50 flex items-center justify-between">
        <div className="flex -space-x-2">
          {/* Mock recent activity indicators */}
          {[1, 2, 3].map(i => (
            <div key={i} className="h-6 w-6 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-[8px] font-bold text-gray-400">
              {i === 1 ? 'L' : i === 2 ? 'T' : 'N'}
            </div>
          ))}
        </div>
        <button className="text-xs font-semibold text-primary-600 hover:text-primary-700">
          View Profile
        </button>
      </div>
    </div>
  );
}

function MemberActions({ member }: { member: TeamMember }) {
  const handleRoleChange = (role: TeamRole) => {
    updateTeamMemberRole(member.id, role);
    window.dispatchEvent(new Event('team_updated'));
  };

  const handleDeactivate = () => {
    if (confirm(`Are you sure you want to deactivate ${member.name}?`)) {
      deactivateTeamMember(member.id);
      window.dispatchEvent(new Event('team_updated'));
    }
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="btn-icon">
        <EllipsisHorizontalIcon className="h-5 w-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="dropdown-menu right-0 mt-2 w-56 z-10">
          <div className="px-3 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-50">
            Change Role
          </div>
          <div className="py-1">
            {(Object.keys(ROLE_CONFIG) as TeamRole[]).map((role) => (
              <Menu.Item key={role}>
                {({ active }) => (
                  <button
                    onClick={() => handleRoleChange(role)}
                    className={clsx(
                      'dropdown-item w-full text-left flex items-center justify-between',
                      active && 'dropdown-item-active',
                      member.role === role && 'text-primary-600 font-bold'
                    )}
                  >
                    {ROLE_CONFIG[role].label}
                    {member.role === role && <ShieldCheckIcon className="h-4 w-4" />}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
          <div className="border-t border-gray-50 py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleDeactivate}
                  className={clsx(
                    'dropdown-item w-full text-left text-red-600',
                    active && 'bg-red-50'
                  )}
                >
                  Deactivate Member
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

export default TeamPage;